import { useAppStore } from '@/store/useAppStore'
import { useNavigate, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/Avatar'
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/Dropdown'
import {
  Home, CheckSquare, Inbox, BarChart2,
  ChevronDown, ChevronRight, Plus, PanelLeftClose, PanelLeft,
  LogOut, Settings, User, UserPlus, Trash2, Archive, ArchiveRestore, Copy, FolderPlus,
  GripVertical
} from 'lucide-react'
import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { SettingsModal } from '@/components/layout/SettingsModal'
import { ProjectSettingsModal } from '@/components/project/ProjectSettingsModal'
import { InviteModal } from '@/components/project/InviteModal'
import { NewClientBoardModal } from '@/components/project/NewProjectModal'
import { useContextMenu } from '@/components/ui/ContextMenu'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

const BASE_NAV = [
  { label: 'Home',       icon: Home,         path: '/home' },
  { label: 'My Tasks',   icon: CheckSquare,  path: '/my-tasks' },
  { label: 'Inbox',      icon: Inbox,        path: '/inbox',   inbox: true },
  { label: 'Reporting',  icon: BarChart2,    path: '/reporting' },
]

function sortByOrder(items, orderArr) {
  if (!orderArr || !orderArr.length) return items
  return [...items].sort((a, b) => {
    const ai = orderArr.indexOf(a.id)
    const bi = orderArr.indexOf(b.id)
    if (ai === -1 && bi === -1) return 0
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

function NavItem({ item, active }) {
  const navigate = useNavigate()
  return (
    <button
      onClick={() => navigate(item.path)}
      className={cn(
        'w-full flex items-center gap-2.5 h-8 rounded-lg text-sm active:scale-[0.97] pl-[10px] pr-3',
        active
          ? 'bg-accent/10 text-accent font-medium'
          : 'text-text-muted hover:text-text hover:bg-surface-raised'
      )}
      style={{
        boxShadow: active ? 'inset 3px 0 0 var(--color-accent)' : 'none',
        transition: 'background 150ms ease, box-shadow 150ms ease, transform 100ms ease-out',
      }}
    >
      <item.icon size={15} className="flex-shrink-0" />
      <span className="flex-1 text-left truncate">{item.label}</span>
      {item.badge && (
        <span className="text-[10px] font-semibold bg-accent text-black rounded-full w-4 h-4 flex items-center justify-center">
          {item.badge}
        </span>
      )}
    </button>
  )
}

function ProjectItem({ project, active, onOpenSettings, onOpenInvite, isArchived, depth = 0, dragListeners }) {
  const navigate = useNavigate()
  const location = useLocation()
  const setActive = useAppStore((s) => s.setActiveProject)
  const deleteProject = useAppStore((s) => s.deleteProject)
  const archiveProject = useAppStore((s) => s.archiveProject)
  const unarchiveProject = useAppStore((s) => s.unarchiveProject)
  const duplicateProject = useAppStore((s) => s.duplicateProject)
  const removeMember = useAppStore((s) => s.removeMember)
  const currentUser = useAppStore((s) => s.currentUser)
  const projects = useAppStore((s) => s.projects)
  const activeProjectId = useAppStore((s) => s.activeProjectId)
  const _projectOrder = useAppStore((s) => s._projectOrder)
  const reorderProjects = useAppStore((s) => s.reorderProjects)
  const isOwner = project.ownerId === currentUser?.id || project.role === 'owner'
  const openCtx = useContextMenu()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [newBoardOpen, setNewBoardOpen] = useState(false)

  const rawChildBoards = projects.filter((p) => p.parentId === project.id && !p.completed)
  const childBoards = sortByOrder(rawChildBoards, _projectOrder?.[project.id])
  const isWorkspace = rawChildBoards.length > 0
  const [childrenOpen, setChildrenOpen] = useState(true)

  const childSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  function handleChildDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = childBoards.map((p) => p.id)
    const newOrder = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id))
    reorderProjects(project.id, newOrder)
  }

  function handleContextMenu(e) {
    openCtx(e, [
      { label: 'Open', onClick: () => { setActive(project.id); navigate(`/project/${project.id}`) } },
      { label: 'Invite', icon: UserPlus, onClick: () => onOpenInvite(project.id) },
      { label: 'Settings', icon: Settings, onClick: () => onOpenSettings(project.id) },
      { label: 'Duplicate', icon: Copy, onClick: () => { duplicateProject(project.id) } },
      ...(!project.parentId ? [{ label: 'Add client board', icon: FolderPlus, onClick: () => setNewBoardOpen(true) }] : []),
      ...(isOwner ? [
        { separator: true },
        ...(isArchived ? [{ label: 'Unarchive', icon: ArchiveRestore, onClick: () => unarchiveProject(project.id) }] : [{ label: 'Archive', icon: Archive, onClick: () => archiveProject(project.id) }]),
        { label: 'Delete project', icon: Trash2, onClick: () => setConfirmDelete(true), danger: true },
      ] : [
        { separator: true },
        { label: 'Leave project', icon: LogOut, onClick: async () => { await removeMember(project.id, currentUser?.id); navigate('/home') }, danger: true },
      ]),
    ])
  }

  function openDeleteConfirm() {
    setDeleteInput('')
    setConfirmDelete(true)
  }

  function handleDelete() {
    if (deleteInput !== project.name) return
    deleteProject(project.id)
    navigate('/my-tasks')
    setConfirmDelete(false)
  }


  const indentPx = depth * 12 + 10

  return (
    <>
      <div className="relative">
        <div className="group/row relative">
          {/* Drag grip — absolutely positioned so it doesn't shift button content */}
          {dragListeners && (
            <button
              {...dragListeners}
              tabIndex={-1}
              className="absolute left-0 top-0 h-8 w-4 opacity-0 group-hover/row:opacity-40 hover:!opacity-80 flex items-center justify-center text-text-faint cursor-grab active:cursor-grabbing touch-none transition-opacity z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical size={11} />
            </button>
          )}

          {/* Collapse chevron — absolutely positioned so it's not nested inside the nav button */}
          {isWorkspace && (
            <button
              onClick={(e) => { e.stopPropagation(); setChildrenOpen((v) => !v) }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={childrenOpen ? 'Collapse project' : 'Expand project'}
              aria-expanded={childrenOpen}
              className="absolute top-0 h-8 w-4 flex items-center justify-center text-text-faint hover:text-text-muted transition-colors active:scale-[0.9] z-10"
              style={{ left: indentPx - 2 }}
            >
              {childrenOpen ? <ChevronDown size={11} aria-hidden="true" /> : <ChevronRight size={11} aria-hidden="true" />}
            </button>
          )}

          <button
            onClick={() => navigate(`/project/${project.id}`)}
            onContextMenu={handleContextMenu}
            className={cn(
              'w-full flex items-center gap-2 h-8 rounded-lg text-sm active:scale-[0.97] pr-3',
              active
                ? 'text-text font-medium'
                : 'text-text-muted hover:text-text hover:bg-surface-raised'
            )}
            style={{
              paddingLeft: isWorkspace ? indentPx + 16 : indentPx,
              background: active ? project.color + '18' : undefined,
              boxShadow: active ? `inset 3px 0 0 ${project.color}` : 'none',
              transition: 'background 150ms ease, box-shadow 150ms ease, transform 100ms ease-out',
            }}
          >
            <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: project.color }} />
            <span className="flex-1 text-left truncate text-[13px]">{project.name}</span>
          </button>
        </div>

        {/* Child boards */}
        <AnimatePresence>
          {isWorkspace && childrenOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
              className="overflow-hidden"
            >
              <DndContext
                sensors={childSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleChildDragEnd}
                modifiers={[restrictToVerticalAxis]}
              >
                <SortableContext items={childBoards.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  {childBoards.map((child) => (
                    <SortableProjectItem
                      key={child.id}
                      project={child}
                      active={activeProjectId === child.id && location.pathname.startsWith('/project')}
                      onOpenSettings={onOpenSettings}
                      onOpenInvite={onOpenInvite}
                      isArchived={false}
                      depth={depth + 1}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <button
                onClick={() => setNewBoardOpen(true)}
                className="w-full flex items-center gap-2 h-7 rounded-lg text-[12px] text-text-faint hover:text-text-muted hover:bg-surface-raised transition-all active:scale-[0.97]"
                style={{ paddingLeft: (depth + 1) * 12 + 10 }}
              >
                <Plus size={11} />
                <span>Add client board</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Delete confirmation modal — portaled to escape dnd-kit stacking contexts */}
      {createPortal(
        <AnimatePresence>
        {confirmDelete && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-sm"
              onClick={() => setConfirmDelete(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9991] w-full max-w-sm mx-4 bg-surface border border-border rounded-2xl shadow-2xl shadow-black/50 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Trash2 size={14} className="text-red-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text">Delete project</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    This will permanently delete <strong>{project.name}</strong> and all its tasks and sections. This cannot be undone.
                  </p>
                </div>
              </div>
              <label className="text-xs text-text-muted mb-1.5 block">
                Type <span className="font-mono text-text">{project.name}</span> to confirm
              </label>
              <input
                autoFocus
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(); if (e.key === 'Escape') setConfirmDelete(false) }}
                placeholder={project.name}
                className="w-full bg-surface-raised border border-border rounded-lg px-3 h-9 text-sm text-text placeholder:text-text-faint focus:outline-none focus:ring-1 focus:ring-red-500/40 mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 h-9 rounded-lg border border-border text-sm text-text-muted hover:text-text transition-colors active:scale-[0.97]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteInput !== project.name}
                  className="flex-1 h-9 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-400 hover:bg-red-500/20 transition-colors active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none"
                >
                  Delete project
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      , document.documentElement)}

      <AnimatePresence>
        {newBoardOpen && (
          <NewClientBoardModal
            parentId={project.id}
            open={newBoardOpen}
            onClose={() => setNewBoardOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function SortableProjectItem({ project, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: project.id })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 150ms cubic-bezier(0.23, 1, 0.32, 1)',
        opacity: isDragging ? 0.45 : 1,
        position: 'relative',
        zIndex: isDragging ? 10 : 0,
      }}
    >
      <ProjectItem {...props} project={project} dragListeners={listeners} />
    </div>
  )
}

export function Sidebar({ onNewProject }) {
  const { currentUser, workspace, projects, activeProjectId, sidebarCollapsed, toggleSidebar, logout, _projectOrder, reorderProjects } = useAppStore()
  const unreadCount = useAppStore((s) => s.notifications.filter((n) => !n.read).length)
  const NAV_ITEMS = BASE_NAV.map((item) => item.inbox ? { ...item, badge: unreadCount || null } : item)
  const navigate = useNavigate()
  const location = useLocation()
  const [projectsOpen, setProjectsOpen] = useState(true)
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settingsSection, setSettingsSection] = useState('Profile')
  const [projectSettingsId, setProjectSettingsId] = useState(null)
  const [projectInviteId, setProjectInviteId] = useState(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const rawActiveProjects = projects.filter((p) => !p.completed && !p.parentId)
  const activeProjects = sortByOrder(rawActiveProjects, _projectOrder?.top)
  const archivedProjects = projects.filter((p) => p.completed && !p.parentId)

  function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = activeProjects.map((p) => p.id)
    reorderProjects(null, arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)))
  }

  const openProjectSettings = useCallback((id) => setProjectSettingsId(id), [])
  const openProjectInvite = useCallback((id) => setProjectInviteId(id), [])

  if (sidebarCollapsed) {
    return (
      <div className="w-12 flex flex-col border-r border-border bg-surface h-full items-center py-4 gap-2 flex-shrink-0">
        <button onClick={toggleSidebar} className="text-text-muted hover:text-text transition-colors p-1 active:scale-[0.97]">
          <PanelLeft size={16} />
        </button>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 240, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
      className="flex flex-col border-r border-border bg-surface h-full flex-shrink-0 overflow-hidden"
      style={{ width: 240 }}
    >
      {/* Workspace header */}
      <div className="flex items-center gap-2 px-3 h-12 border-b border-border flex-shrink-0">
        <div className="w-7 h-7 rounded-lg bg-accent/20 flex items-center justify-center text-accent flex-shrink-0">
          <svg viewBox="0 0 16 16" fill="none" className="w-[13px] h-[13px]">
            <rect x="1" y="1" width="4" height="14" rx="2" fill="currentColor"/>
            <rect x="7" y="1" width="8" height="6" rx="2" fill="currentColor"/>
            <rect x="7" y="9" width="8" height="6" rx="2" fill="currentColor" opacity="0.38"/>
          </svg>
        </div>
        <span className="font-semibold text-sm text-text flex-1 truncate">{workspace?.name}</span>
        <button onClick={toggleSidebar} className="text-text-faint hover:text-text-muted transition-colors active:scale-[0.97]">
          <PanelLeftClose size={15} />
        </button>
      </div>

      {/* Nav */}
      <div data-tour="sidebar" className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.path} item={item} active={location.pathname === item.path} />
        ))}

        <div className="my-2 border-t border-border" />

        {/* Projects */}
        <div>
          <button
            onClick={() => setProjectsOpen((v) => !v)}
            className="w-full flex items-center gap-1.5 px-3 h-7 text-[11px] font-semibold uppercase tracking-wider text-text-faint hover:text-text-muted transition-colors group"
          >
            {projectsOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            Projects
            <button
              onClick={(e) => { e.stopPropagation(); onNewProject?.() }}
              className="ml-auto text-text-faint hover:text-text-muted active:scale-[0.97]"
              title="New project"
            >
              <Plus size={13} />
            </button>
          </button>

          <AnimatePresence>
            {projectsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                className="overflow-hidden"
              >
                <div className="space-y-0.5 mt-0.5">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToVerticalAxis]}
                  >
                    <SortableContext items={activeProjects.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                      {activeProjects.map((p) => (
                        <SortableProjectItem
                          key={p.id}
                          project={p}
                          active={activeProjectId === p.id && location.pathname.startsWith('/project')}
                          onOpenSettings={openProjectSettings}
                          onOpenInvite={openProjectInvite}
                          isArchived={false}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  <button
                    onClick={onNewProject}
                    className="w-full flex items-center gap-2.5 px-3 h-8 rounded-lg text-sm text-text-faint hover:text-text-muted hover:bg-surface-raised transition-all active:scale-[0.97]"
                  >
                    <Plus size={13} />
                    <span className="text-[13px]">New project</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Archived projects */}
        {archivedProjects.length > 0 && (
          <div className="mt-1">
            <button
              onClick={() => setArchivedOpen((v) => !v)}
              className="w-full flex items-center gap-1.5 px-3 h-7 text-[11px] font-semibold uppercase tracking-wider text-text-faint hover:text-text-muted transition-colors"
            >
              {archivedOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
              Archived
              <span className="ml-auto text-[10px] font-normal normal-case tracking-normal opacity-60">{archivedProjects.length}</span>
            </button>
            <AnimatePresence>
              {archivedOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
                  className="overflow-hidden"
                >
                  <div className="space-y-0.5 mt-0.5 opacity-60">
                    {archivedProjects.map((p) => (
                      <ProjectItem
                        key={p.id}
                        project={p}
                        active={activeProjectId === p.id && location.pathname.startsWith('/project')}
                        onOpenSettings={openProjectSettings}
                        onOpenInvite={openProjectInvite}
                        isArchived={true}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* User footer */}
      <div className="px-2 py-2 border-t border-border">
        <Dropdown
          align="left"
          direction="up"
          trigger={
            <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-raised transition-colors active:scale-[0.97]">
              <Avatar user={currentUser} size="sm" />
              <span className="text-xs text-text-muted truncate flex-1 text-left">{currentUser?.name}</span>
            </button>
          }
        >
          <DropdownItem icon={User} label="Profile" onClick={() => navigate('/profile')} />
          <DropdownItem icon={Settings} label="Settings" onClick={() => { setSettingsSection('Workspace'); setSettingsOpen(true) }} />
          <DropdownSeparator />
          <DropdownItem
            icon={LogOut}
            label="Log out"
            danger
            onClick={() => { logout(); navigate('/auth') }}
          />
        </Dropdown>
      </div>
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} initialSection={settingsSection} />
      <ProjectSettingsModal projectId={projectSettingsId} open={!!projectSettingsId} onClose={() => setProjectSettingsId(null)} />
      <AnimatePresence>
        {projectInviteId && <InviteModal projectId={projectInviteId} onClose={() => setProjectInviteId(null)} />}
      </AnimatePresence>
    </motion.div>
  )
}
